/**
 * Shared contact/enquiry contract.
 *
 * This module is imported by BOTH the browser form and the server handler so
 * that validation rules can never drift between the two. Keep it dependency
 * free and side-effect free.
 */

/** Nepal country code, pre-filled and locked in the phone field. */
export const DEFAULT_COUNTRY_CODE = "+977";

export const TIME_SLOTS = [
  "Morning (6am – 11am)",
  "Afternoon (11am – 4pm)",
  "Evening (4pm – 9pm)",
  "Flexible / any time",
] as const;

export const ENQUIRY_TYPES = [
  "Bridal",
  "Party & Events",
  "Editorial & Portfolio",
  "Everyday Glamour",
  "Something else",
] as const;

export type EnquiryType = (typeof ENQUIRY_TYPES)[number];

export type Submission = {
  id: number;
  name: string;
  phone: string;
  /** Free text — the slot label, or whatever the client typed. */
  preferredTime: string;
  enquiryType: string;
  message: string;
  /** Human-readable place, typed by hand or reverse-geocoded. */
  location: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  /** False once an archived row is restored. */
  read: number;
};

export type SubmissionInput = {
  name: string;
  phone: string;
  preferredTime?: string;
  enquiryType?: string;
  message?: string;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Honeypot: bots fill hidden inputs, humans never see it. */
  company?: string;
};

export type ValidationResult =
  | { ok: true; value: Required<Pick<SubmissionInput, "name" | "phone">> & SubmissionInput }
  | { ok: false; errors: Record<string, string> };

const MAX = { name: 80, phone: 24, time: 80, message: 2000, location: 160 };

/**
 * Strips formatting characters and returns digits only.
 * Used to reject things like "abc" or "12" masquerading as a phone number.
 */
export function digitsOnly(value: string) {
  return value.replace(/\D+/g, "");
}

/**
 * A phone number is accepted if it has 7–15 digits (E.164 allows up to 15),
 * which covers every real numbering plan including Nepal's 10-digit mobiles.
 */
export function isValidPhone(value: string) {
  const digits = digitsOnly(value);
  return digits.length >= 7 && digits.length <= 15;
}

export function validateSubmission(raw: unknown): ValidationResult {
  const errors: Record<string, string> = {};
  const input = (raw ?? {}) as Record<string, unknown>;

  const str = (key: string, max: number) => {
    const value = typeof input[key] === "string" ? (input[key] as string).trim() : "";
    return value.slice(0, max);
  };

  const name = str("name", MAX.name);
  if (!name) errors.name = "Please tell me your name.";
  else if (name.length < 2) errors.name = "That name looks a little short.";

  const phone = str("phone", MAX.phone);
  if (!phone) errors.phone = "A phone number is required so I can reply.";
  else if (!isValidPhone(phone)) errors.phone = "Please enter a valid phone number.";

  const lat = typeof input.latitude === "number" ? input.latitude : null;
  const lon = typeof input.longitude === "number" ? input.longitude : null;

  // Reject impossible coordinates rather than storing junk.
  if (lat !== null && (Number.isNaN(lat) || Math.abs(lat) > 90)) {
    errors.location = "Those coordinates do not look right.";
  }
  if (lon !== null && (Number.isNaN(lon) || Math.abs(lon) > 180)) {
    errors.location = "Those coordinates do not look right.";
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      name,
      phone,
      preferredTime: str("preferredTime", MAX.time),
      enquiryType: str("enquiryType", 60),
      message: str("message", MAX.message),
      location: str("location", MAX.location),
      latitude: lat,
      longitude: lon,
      company: str("company", 100),
    },
  };
}
